import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Filter } from 'lucide-react';

export function TechnicianMultiFilter({ options, selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const [coords, setCoords] = React.useState({ top: 0, left: 0, width: 256 });

  const reposition = React.useCallback(() => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 256) });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    reposition();
    const onClick = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onScroll = () => reposition();
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, reposition]);

  const toggle = (t) => {
    const set = new Set(selected);
    if (set.has(t)) set.delete(t); else set.add(t);
    onChange([...set]);
  };

  const summary = selected.length === 0
    ? 'All Technicians'
    : selected.length === 1 ? selected[0]
    : `${selected.length} technicians`;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        data-testid="recon-tech-multi"
        className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[12.5px] text-slate-700 inline-flex items-center gap-1.5 hover:border-sky-300"
      >
        <Filter size={12} /> {summary} <ChevronDown size={12} />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, zIndex: 9999 }}
          className="max-h-72 overflow-y-auto rounded-xl bg-white border border-slate-200 shadow-2xl p-2"
        >
          <div className="flex items-center justify-between px-2 py-1 mb-1">
            <span className="text-[11px] uppercase tracking-wider text-slate-400">Technicians</span>
            <button onClick={() => onChange([])} className="text-[11.5px] text-sky-600 hover:underline">Clear</button>
          </div>
          {options.length === 0 ? (
            <p className="text-[12px] text-slate-400 px-2 py-2">No technicians in result</p>
          ) : options.map((t) => (
            <label key={t} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(t)}
                onChange={() => toggle(t)}
                data-testid={`recon-tech-opt-${t}`}
                className="w-4 h-4 rounded border-slate-300 text-sky-600"
              />
              <span className="text-[12.5px] text-slate-700">{t}</span>
            </label>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
